import { inject, Injectable } from '@angular/core';
import { ObjectState, StatefullValue } from '@meddev/fe-shared';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import { DeviceObservationResponseDto } from '../../model/device/device-observation-response-dto';
import { DeviceResponseDto } from '../../model/device/device-response-dto';
import { LabisExaminationRequestDto } from '../../model/laboratory/lab-examination-request-dto';
import { ContractorResponseDto } from '../../model/vendor/contractor-response-dto';
import { VendorClientService } from '../../services/clients/vendor-client.service';
import { BaseState } from '../base/base.state';
import {
    LoadContractorDevices,
    LoadDeviceObservations,
    LoadDeviceVendorContractors,
    SetSelectedContractor,
    SetSelectedDevice,
    SetSelectedDeviceObservation,
    SubmitDeviceOrder,
} from './device-actions';
import { DeviceStateModel } from './device-state.model';

const DEVICE_STATE_TOKEN: StateToken<DeviceStateModel> = new StateToken('devicestate');

const DEFAULT_STATE: DeviceStateModel = {
    selectedContractorId: { state: ObjectState.UNINIT, value: null } as StatefullValue<number>,
    selectedDeviceId: { state: ObjectState.UNINIT, value: null } as StatefullValue<string>,
    selectedDeviceObservationId: { state: ObjectState.UNINIT, value: null } as StatefullValue<string>,
    deviceVendorContractors: { state: ObjectState.UNINIT, value: null } as StatefullValue<ContractorResponseDto[]>,
    contractorDevices: { state: ObjectState.UNINIT, value: null } as StatefullValue<DeviceResponseDto[]>,
    selectedDeviceObservations: { state: ObjectState.UNINIT, value: null } as StatefullValue<DeviceObservationResponseDto[]>,
    submit: { state: ObjectState.VALID, value: false } as StatefullValue<boolean>,
};

@State<DeviceStateModel>({
    name: DEVICE_STATE_TOKEN,
    defaults: DEFAULT_STATE,
    children: [],
})
@Injectable()
export class DeviceState {
    private store = inject(Store);
    private vendorClientService = inject(VendorClientService);

    @Selector([DEVICE_STATE_TOKEN])
    public static selectedDeviceId$(state: DeviceStateModel): StatefullValue<string> {
        return state.selectedDeviceId;
    }

    @Selector([DEVICE_STATE_TOKEN])
    public static selectedDeviceObservationId$(state: DeviceStateModel): StatefullValue<string> {
        return state.selectedDeviceObservationId;
    }

    @Selector([DEVICE_STATE_TOKEN])
    public static deviceVendorContractors$(state: DeviceStateModel): StatefullValue<ContractorResponseDto[]> {
        return state.deviceVendorContractors;
    }

    @Selector([DEVICE_STATE_TOKEN])
    public static contractorDevices$(state: DeviceStateModel): StatefullValue<DeviceResponseDto[]> {
        return state.contractorDevices;
    }

    @Selector([DEVICE_STATE_TOKEN])
    public static contractorDeviceObservations$(state: DeviceStateModel): StatefullValue<DeviceObservationResponseDto[]> {
        return state.selectedDeviceObservations;
    }

    @Selector([DEVICE_STATE_TOKEN])
    public static submitOrder$(state: DeviceStateModel): StatefullValue<boolean> {
        return state.submit;
    }

    @Action(SetSelectedContractor)
    public setSelectedContractor$(ctx: StateContext<DeviceStateModel>, action: SetSelectedContractor): Observable<StatefullValue<number>> {
        return of(action).pipe(
            map(() => {
                ctx.patchState({ selectedContractorId: { state: ObjectState.VALID, value: action.selectedContractorId } });
                return ctx.getState().selectedContractorId;
            }),
        );
    }

    @Action(SetSelectedDevice)
    public setSelectedDevice$(ctx: StateContext<DeviceStateModel>, action: SetSelectedDevice): Observable<unknown> {
        return of(action).pipe(
            tap(() =>
                ctx.patchState({
                    selectedDeviceId: { state: ObjectState.UNINIT, value: null },
                }),
            ),
            map(() => {
                ctx.patchState({
                    selectedDeviceId: { state: action.selectedDeviceId ? ObjectState.VALID : ObjectState.UNINIT, value: action.selectedDeviceId },
                });
                return ctx.getState().selectedDeviceId;
            }),
            tap(() => {
                ctx.dispatch(new LoadDeviceObservations(action.selectedDeviceId));
            }),
        );
    }

    @Action(SetSelectedDeviceObservation)
    public setSelectedDeviceObservation(ctx: StateContext<DeviceStateModel>, action: SetSelectedDeviceObservation): Observable<unknown> {
        return of(action).pipe(
            tap(() =>
                ctx.patchState({
                    selectedDeviceObservationId: { state: ObjectState.UNINIT, value: null },
                }),
            ),
            map(() => {
                ctx.patchState({
                    selectedDeviceObservationId: {
                        state: action.selectedDeviceObservationId ? ObjectState.VALID : ObjectState.UNINIT,
                        value: action.selectedDeviceObservationId,
                    },
                });
                return ctx.getState().selectedDeviceObservationId;
            }),
        );
    }

    @Action(LoadDeviceVendorContractors)
    public loadDeviceVendorContractors$(ctx: StateContext<DeviceStateModel>): Observable<StatefullValue<ContractorResponseDto[]>> {
        return this.store.select(BaseState.activeContractorId).pipe(
            tap(() => ctx.patchState({ deviceVendorContractors: { state: ObjectState.LOADING, value: null } })),
            switchMap(contractorId => {
                if (contractorId) {
                    return this.vendorClientService.getDeviceVendorContractors(contractorId);
                }
                throw new Error();
            }),
            map(response => {
                ctx.patchState({ deviceVendorContractors: { state: ObjectState.VALID, value: response } });
                return ctx.getState().deviceVendorContractors;
            }),
            catchError(() => {
                ctx.patchState({ deviceVendorContractors: { state: ObjectState.ERROR, value: null } });
                return of(ctx.getState().deviceVendorContractors);
            }),
            tap(() => {
                ctx.dispatch(new LoadContractorDevices());
            }),
        );
    }

    @Action(LoadContractorDevices)
    public loadVendorDevices$(ctx: StateContext<DeviceStateModel>): Observable<StatefullValue<DeviceResponseDto[]>> {
        return this.store.select(BaseState.activeContractorId).pipe(
            tap(() => ctx.patchState({ contractorDevices: { state: ObjectState.LOADING, value: null } })),
            switchMap(contractorId => {
                const deviceVendorContractors = ctx.getState().deviceVendorContractors?.value ?? [];
                if (contractorId && deviceVendorContractors.length > 0) {
                    const devicesPerContractor$ = deviceVendorContractors.map(vendorContractor =>
                        this.vendorClientService
                            .getContractorDevices(contractorId, vendorContractor.id)
                            .pipe(
                                map(devices => devices.map(device => ({ ...device, vendorContractorId: vendorContractor.id }) as DeviceResponseDto)),
                            ),
                    );
                    return forkJoin(devicesPerContractor$).pipe(map(deviceLists => deviceLists.flat()));
                }
                throw new Error();
            }),
            map(response => {
                ctx.patchState({ contractorDevices: { state: ObjectState.VALID, value: response } });
                return ctx.getState().contractorDevices;
            }),
            catchError(() => {
                ctx.patchState({ contractorDevices: { state: ObjectState.ERROR, value: null } });
                return of(ctx.getState().contractorDevices);
            }),
        );
    }

    @Action(LoadDeviceObservations)
    public loadDeviceObservations$(
        ctx: StateContext<DeviceStateModel>,
        action: LoadDeviceObservations,
    ): Observable<StatefullValue<DeviceObservationResponseDto[]>> {
        return this.store.select(BaseState.activeContractorId).pipe(
            tap(() => ctx.patchState({ selectedDeviceObservations: { state: ObjectState.LOADING, value: null } })),
            switchMap(contractorId => {
                const matchedDevice = ctx.getState().contractorDevices?.value?.find(device => device.externalId === action.deviceId);
                if (contractorId && matchedDevice?.vendorContractorId && action.deviceId) {
                    return this.vendorClientService.getDeviceObservations(contractorId, matchedDevice.vendorContractorId, action.deviceId);
                }
                ctx.patchState({ selectedDeviceObservations: { state: ObjectState.UNINIT, value: null } });
                return EMPTY;
            }),
            map(response => {
                ctx.patchState({ selectedDeviceObservations: { state: ObjectState.VALID, value: response } });
                return ctx.getState().selectedDeviceObservations;
            }),
            catchError(() => {
                ctx.patchState({ selectedDeviceObservations: { state: ObjectState.ERROR, value: null } });
                return of(ctx.getState().selectedDeviceObservations);
            }),
        );
    }

    @Action(SubmitDeviceOrder)
    public submitDeviceOrder$(ctx: StateContext<DeviceStateModel>, action: SubmitDeviceOrder): Observable<StatefullValue<boolean>> {
        return this.store.select(BaseState.activeContractorId).pipe(
            first(),
            tap(() => ctx.patchState({ submit: { state: ObjectState.LOADING, value: false } })),
            switchMap(contractorId =>
                this.vendorClientService.createLabisReservationExamination(contractorId, action.preReservationId, {
                    labisContractorId: action.contractorId,
                    deviceId: action.deviceId,
                    observationIds: action.observationIds,
                } as LabisExaminationRequestDto),
            ),
            map(() => {
                ctx.patchState({ submit: { state: ObjectState.VALID, value: false } });
                return ctx.getState().submit;
            }),
            catchError(err => {
                console.error(err);
                ctx.patchState({ submit: { state: ObjectState.ERROR, value: false } });
                return throwError(() => err);
            }),
        );
    }
}
